import React, { Fragment, useEffect, useState } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
} from 'reactstrap';
import ReactTable from 'react-table';
import DataTablePagination from '../../../../../../components/DatatablePagination';
import IntlMessages from '../../../../../../helpers/IntlMessages';
import { Colxx } from '../../../../../../components/common/CustomBootstrap';
import { AxiosAPI, getGroupID } from '../../../../../../helpers/AxiosAPI';
import { postAPI_Teams_Stats } from '../../../../../../constants/APIs';
import EmailStatistics from '../EmailStats/emailStatistics';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import { AreaChart } from '../../charts';
import { ThemeColors } from '../../../../../../helpers/ThemeColors';
import {
  week,
  month,
  last7Days,
  last30Days,
  last60Days,
  last90Days,
  LastDays,
  year,
} from './../lablesConfig';
const colors = ThemeColors();

const TeamsStats = ({ teamsIntegrations, propertyId, timeZone }) => {
  const [stats, setStats] = useState();
  const [error, setError] = useState(null);
  const [labels, setLabels] = useState(last7Days);
  const [startDate, setStartDate] = useState(moment().subtract(7, 'days'));
  const [endDate, setEndDate] = useState(moment());
  const [buttonText, setButtonText] = useState(<IntlMessages id="dashboards.last-7-days" />);
  const [isShow, setIsShow] = useState(false);
  const [integrationList, setIntegrationList] = useState([]);
  const [filterIntegrationList, setFilterIntegrationList] = useState([]);
  const [integrationId, setIntegrationId] = useState(null);
  const [integrationName, setIntegrationName] = useState('');

  const fetchTeamsStats = (sDate = startDate, eDate = endDate, selectedIntegrationId = integrationId) => {
    const integrationsPayload = selectedIntegrationId
      ? [selectedIntegrationId]
      : teamsIntegrations?.length > 0
        ? teamsIntegrations.map((m) => m.id)
        : [];

    const payload = {
      groupId: getGroupID(),
      propertyId: propertyId === '-1' ? null : propertyId,
      integrations: integrationsPayload,
      dateFrom: sDate ? moment(sDate).format('YYYY-MM-DD') + ' 00:00:00' : null,
      dateTo: eDate ? moment(eDate).format('YYYY-MM-DD') + ' 23:59:59' : null,
    };

    // if date range is more than 90 days, request aggregated by month from API
    try {
      const fromMoment = sDate ? moment(sDate) : null;
      const toMoment = eDate ? moment(eDate) : null;
      const diffDays = fromMoment && toMoment ? toMoment.diff(fromMoment, 'days') : 0;
      if (diffDays > 90) {
        payload.isMonth = true;
      }
    } catch (e) {
      // silently ignore if moment parsing fails
    }

    AxiosAPI({
      method: 'post',
      url: `${postAPI_Teams_Stats}`,
      data: payload,
    })
      .then((res) => {
        setStats(res.data);
      })
      .catch((err) => {
        setError('Failed to load Teams stats');
      });
  };

  useEffect(() => {
    if (teamsIntegrations !== undefined && teamsIntegrations.length > 0) {
      fetchTeamsStats();
    }
  }, [teamsIntegrations, propertyId]);

  useEffect(() => {
    // populate integration lists for dropdown from prop
    if (teamsIntegrations !== undefined && teamsIntegrations.length > 0) {
      setIntegrationList(teamsIntegrations);
      setFilterIntegrationList(teamsIntegrations);
    } else {
      setIntegrationList([]);
      setFilterIntegrationList([]);
    }
  }, [teamsIntegrations]);

  const IntegrationClick = (id, name) => {
    setIntegrationId(id);
    setIntegrationName(name);
    fetchTeamsStats(startDate, endDate, id);
  };

  const selectLast7Days = () => {
    setButtonText(<IntlMessages id="dashboards.last-7-days" />);
    const from = moment().subtract(7, 'days');
    const to = moment();
    setStartDate(from);
    setEndDate(to);
    setLabels(last7Days);
    fetchTeamsStats(from, to, integrationId);
  };

  const selectLast30Days = () => {
    setButtonText(<IntlMessages id="dashboards.last-30-days" />);
    const from = moment().subtract(30, 'days');
    const to = moment();
    setStartDate(from);
    setEndDate(to);
    setLabels(last30Days);
    fetchTeamsStats(from, to, integrationId);
  };

  const selectLast60Days = () => {
    setButtonText(<IntlMessages id="dashboards.last-60-days" />);
    const from = moment().subtract(60, 'days');
    const to = moment();
    setStartDate(from);
    setEndDate(to);
    setLabels(last60Days);
    fetchTeamsStats(from, to, integrationId);
  };

  const selectLast90Days = () => {
    setButtonText(<IntlMessages id="dashboards.last-90-days" />);
    const from = moment().subtract(90, 'days');
    const to = moment();
    setStartDate(from);
    setEndDate(to);
    setLabels(last90Days);
    fetchTeamsStats(from, to, integrationId);
  };

  const selectThisWeek = () => {
    setButtonText(<IntlMessages id="dashboards.this-week" />);
    const from = moment().startOf('isoWeek');
    const to = moment().endOf('isoWeek');
    setStartDate(from);
    setEndDate(to);
    setLabels(week);
    fetchTeamsStats(from, to, integrationId);
  };

  const selectThisMonth = () => {
    setButtonText(<IntlMessages id="dashboards.this-month" />);
    const from = moment().startOf('month');
    const to = moment();
    setStartDate(from);
    setEndDate(to);
    setLabels(month);
    fetchTeamsStats(from, to, integrationId);
  };

  const selectPreviousMonth = () => {
    setButtonText(<IntlMessages id="dashboards.previous-month" />);
    const from = moment().subtract(1, 'months').startOf('month');
    const to = moment().subtract(1, 'months').endOf('month');
    setStartDate(from);
    setEndDate(to);
    setLabels(month);
    fetchTeamsStats(from, to, integrationId);
  };

  const selectThisYear = () => {
    setButtonText(<IntlMessages id="dashboards.yeartodate" />);
    const from = moment().startOf('year');
    const to = moment();
    setStartDate(from);
    setEndDate(to);
    setLabels(year);
    fetchTeamsStats(from, to, integrationId);
  };

  const selectLastYear = () => {
    setButtonText(<IntlMessages id="dashboards.lastyear" />);
    const from = moment().subtract(1, 'years').startOf('year');
    const to = moment().subtract(1, 'years').endOf('year');
    setStartDate(from);
    setEndDate(to);
    setLabels(year);
    fetchTeamsStats(from, to, integrationId);
  };

  useEffect(() => {
    // refetch when date range changes
    if (teamsIntegrations !== undefined && teamsIntegrations.length > 0) {
      fetchTeamsStats(startDate, endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  if (error) {
    return (
      <Card className="text-center my-3" style={{ padding: '50px' }}>
        <p style={{ color: 'red' }}>{error}</p>
      </Card>
    );
  }
  console.error('TeamsStats labels:', labels);
  return (
    <Fragment>
      <Row>
        <Col xxs="12" className="mb-2">
          <Fragment>
            <div className="btn-group float-right float-none-xs mb-3">
              <UncontrolledDropdown className="ml-2 mr-2">
                <DropdownToggle caret color="primary" className="btn-xs" outline>
                  <span className="name">
                    <b>{integrationName === '' ? 'All' : integrationName}</b>
                  </span>
                </DropdownToggle>
                <DropdownMenu style={{ maxHeight: '200px', overflowY: 'auto' }} className="mt-3" right>
                  <div className="search-property">
                    <input type="text" placeholder="Search" onKeyUp={(e) => {
                      const serachtext = e.target.value.toLowerCase();
                      const resdata = integrationList.filter((a) => a.name.toLowerCase().includes(serachtext));
                      setFilterIntegrationList(resdata);
                    }} />
                  </div>
                  <DropdownItem onClick={() => IntegrationClick(null, '')}>All</DropdownItem>
                  {filterIntegrationList != undefined && filterIntegrationList !== null && filterIntegrationList.length > 0 &&
                    filterIntegrationList.map((l) => (
                      <DropdownItem onClick={() => IntegrationClick(l.id, l.name)} key={l.id}>
                        {l.name}
                      </DropdownItem>
                    ))}
                </DropdownMenu>
              </UncontrolledDropdown>

              <UncontrolledDropdown>
                <DropdownToggle caret color="primary" className="btn-xs" outline>
                  {buttonText}
                </DropdownToggle>
                <DropdownMenu right>
                  <DropdownItem onClick={selectLast7Days}><IntlMessages id="dashboards.last-7-days" /></DropdownItem>
                  <DropdownItem onClick={selectLast30Days}><IntlMessages id="dashboards.last-30-days" /></DropdownItem>
                  <DropdownItem onClick={selectLast60Days}><IntlMessages id="dashboards.last-60-days" /></DropdownItem>
                  <DropdownItem onClick={selectLast90Days}><IntlMessages id="dashboards.last-90-days" /></DropdownItem>
                  <DropdownItem onClick={selectThisWeek}><IntlMessages id="dashboards.this-week" /></DropdownItem>
                  <DropdownItem onClick={selectThisMonth}><IntlMessages id="dashboards.this-month" /></DropdownItem>
                  <DropdownItem onClick={selectPreviousMonth}><IntlMessages id="dashboards.previous-month" /></DropdownItem>
                  <DropdownItem onClick={selectThisYear}><IntlMessages id="dashboards.yeartodate" /></DropdownItem>
                  <DropdownItem onClick={selectLastYear}><IntlMessages id="dashboards.lastyear" /></DropdownItem>
                  <DropdownItem onClick={() => setIsShow(!isShow)}><IntlMessages id="form-components.date-range" /></DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          </Fragment>
        </Col>
      </Row>

      <Modal isOpen={isShow} toggle={() => setIsShow(!isShow)} style={{ borderRadius: '10px' }}>
        <ModalHeader toggle={() => setIsShow(!isShow)}><IntlMessages id="form-components.date-range" /></ModalHeader>
        <ModalBody>
          <Row>
            <Col sm="6" className="mb-2">
              <FormGroup>
                <Label><IntlMessages id='dashboards.start-date' /></Label>
                <DatePicker selected={startDate ? new Date(startDate) : null} onChange={(date) => setStartDate(moment(date))} placeholderText="Start Date" dateFormat="dd/MM/yyyy" />
              </FormGroup>
            </Col>
            <Col sm="6" className="mb-2">
              <FormGroup>
                <Label><IntlMessages id='dashboards.end-date' /></Label>
                <DatePicker selected={endDate ? new Date(endDate) : null} onChange={(date) => setEndDate(moment(date))} placeholderText="End Date" dateFormat="dd/MM/yyyy" />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color='primary' onClick={() => { setIsShow(false); fetchTeamsStats(startDate, endDate, integrationId); }}>{<IntlMessages id='form-components.submit' /> || 'Submit'}</Button>
          <Button className="ml-2" onClick={() => setIsShow(!isShow)}>Close</Button>
        </ModalFooter>
      </Modal>

      {/* Row with two area charts: Conversations and Messages */}
      <Row>
        <Col sm="6">
          <Card className="p-3 mt-3">
            {stats !== undefined && stats?.conversationStats &&
              <CardBody>
                {(() => {                  
                  const areaChartData = {
                    labels,
                    datasets: [
                      {
                        label: 'conversations',
                        data: stats?.conversationStats?.map(elem => elem?.count || 0) || [],
                        borderColor: colors.themeColor1,
                        pointBackgroundColor: colors.foregroundColor,
                        pointBorderColor: colors.themeColor1,
                        pointHoverBackgroundColor: colors.themeColor1,
                        pointHoverBorderColor: colors.foregroundColor,
                        pointRadius: 4,
                        pointBorderWidth: 2,
                        pointHoverRadius: 5,
                        fill: false,
                        borderWidth: 2,
                        backgroundColor: colors.themeColor1_10,
                      },
                    ],
                  };

                  return (
                    <div>
                      <h5>
                        <IntlMessages id="dashboards.conversations" />
                      </h5>
                      <div className="chart card-body pt-0">
                        <AreaChart shadow data={areaChartData} />
                      </div>
                    </div>
                  );
                })()}
              </CardBody>}
          </Card>
        </Col>

        <Col sm="6">
          <Card className="p-3 mt-3">
            {stats?.messageStats &&
              <CardBody>
                  {(() => {
                    const msgSeries =
                      stats?.messageByDate ||
                      stats?.messagesByDate ||
                      stats?.messageStats ||
                      stats?.messageStatsData ||
                      stats?.messageOverTime ||
                      [];

                    const messageData = msgSeries && msgSeries.length > 0 ? msgSeries.map((s) => s.count ?? s.messages ?? s.value ?? 0) : [];

                    const areaChartData = {
                      labels, // use component labels state
                      datasets: [
                        {
                          label: 'messages',
                          data: messageData,
                          borderColor: colors.themeColor2,
                          pointBackgroundColor: colors.foregroundColor,
                          pointBorderColor: colors.themeColor2,
                          pointHoverBackgroundColor: colors.themeColor2,
                          pointHoverBorderColor: colors.foregroundColor,
                          pointRadius: 4,
                          pointBorderWidth: 2,
                          pointHoverRadius: 5,
                          fill: true,
                          borderWidth: 2,
                          backgroundColor: colors.themeColor2_10,
                        },
                      ],
                    };

                    return (
                      <div>
                        <h5>
                          <IntlMessages id="dashboards.total-messages" />
                        </h5>
                        <div className="chart card-body pt-0">
                          <AreaChart shadow data={areaChartData} />
                        </div>
                      </div>
                    );
                  })()}
              </CardBody>}
          </Card>
        </Col>
      </Row>

      {/* Full-width table using react-table with Email / Conversation / Message columns */}
      <Row>
        <Col sm="12">
          <Card className="p-3 mt-3">
            {stats !== undefined &&
              <CardBody>
                {(() => {
                  const tableData = stats
                    ? Array.isArray(stats?.messageByEmail) && stats?.messageByEmail.length > 0
                      ? stats?.messageByEmail.map((it) => ({
                        email: it.email || '-',
                        conversation: it.conversations || '-',
                        message: it.messages || '-',
                      }))
                      : [
                        {
                          email: stats.totalEmails ?? stats.emails ?? '-',
                          conversation: stats.totalConversations ?? stats.conversations ?? stats.totalReplies ?? '-',
                          message: stats.totalMessages ?? stats.messages ?? 0,
                        },
                      ]
                    : Array.from({ length: 1 }).map(() => ({ email: '', conversation: '', message: '' }));

                  const columns = [
                    {
                      Header: 'Email',
                      accessor: 'email',
                      Cell: (cell) => (cell.value ? <span className='ml-3'>{cell.value}</span> : <span></span>),
                    },
                    {
                      Header: 'Conversation',
                      accessor: 'conversation',
                      Cell: (cell) => (cell.value ? <span className='ml-3'>{cell.value}</span> : <span></span>),
                    },
                    {
                      Header: 'Message',
                      accessor: 'message',
                      Cell: (cell) => (cell.value ? <span className='ml-3'>{cell.value}</span> : <span></span>),
                    },
                  ];

                  return (
                    <ReactTable
                      data={tableData}
                      columns={columns}
                      defaultPageSize={5}
                      filterable={false}
                      showPageJump={true}
                      PaginationComponent={DataTablePagination}
                      showPageSizeOptions={true}
                      defaultSorted={[{ id: 'message', desc: true }]}
                    />
                  );
                })()}
              </CardBody>}
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};  

export default TeamsStats;
